import { computed, Inject, Injectable, Type, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { lastValueFrom } from 'rxjs';
import {
  AlertDialogComponent,
  AlertDialogInput,
  AlertDialogResult,
  Message,
  MessageType,
} from '../components/alert-dialog/alert-dialog.component';
export { MessageType } from '../components/alert-dialog/alert-dialog.component';
import {
  ConfirmDialogInput,
  ConfirmDialogResult,
  ConfirmDialogComponent,
} from '../components/confirm-dialog/confirm-dialog.component';
import {
  SessionExpiredDialogInput,
  SessionExpiredDialogResult,
  SessionExpiredDialogComponent,
} from '../components/session-expired-dialog/session-expired-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { formatError, FriendlyError, isFirebaseFunctionsError } from '../../../core/utils';
import {
  WaitDialogInput,
  WaitDialogResult,
  WaitDialogComponent,
} from 'src/app/features/shared/components/wait-dialog/wait-dialog.component';
import { createDeferred } from 'src/features/common/utils';
import { DIALOG_STACK_SIGNAL } from '../../../core/core.module';

/**
 * Infer signature based on Input/Output values
 */
// prettier-ignore
type DialogFn<Input, Output> = Input extends undefined
  ? Output extends undefined
    ? () => Promise<undefined>
    : () => Promise<Output | undefined>
  : Output extends undefined
    ? (data: Input) => Promise<undefined>
    : (data: Input) => Promise<Output | undefined>;

@Injectable()
export class DialogService {
  public isDialogOpen = computed(() => this.dialogStack() > 0);

  constructor(
    private readonly dialog: MatDialog,
    @Inject(DIALOG_STACK_SIGNAL)
    private readonly dialogStack: WritableSignal<number>,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
  ) {}

  public async alert(
    message: Message,
    type: MessageType = MessageType.Info,
    details?: string,
  ): Promise<void> {
    await this.open<AlertDialogInput, AlertDialogResult>(AlertDialogComponent, {
      data: { message, type, details },
      autoFocus: false,
    });
  }

  /**
   * Factory for dialog service methods
   *
   * This functions returns a functions that can be assigned to a class
   * public properties (so they work as methods). This removes most of
   * the boilerplate from dialog generics.
   *
   * @param component Dialog class to open.
   * @param config MatDialog configuration, same as in regular `MatDialog.open`. `data` will get injected per call.
   */
  public createOpenDialogFn<Input, Output>(
    component: Type<unknown>,
    config: MatDialogConfig<Input> | ((data?: Input) => MatDialogConfig<Input>) = {},
  ): DialogFn<Input, Output> {
    return ((data?: Input) => {
      if (typeof config === 'function') {
        config = config(data);
      }

      return this.open<Input, Output>(component, {
        ...config,
        data: data,
      });
    }) as unknown as DialogFn<Input, Output>;
  }

  public async confirm(message: string): Promise<boolean> {
    return this.open<ConfirmDialogInput, ConfirmDialogResult>(ConfirmDialogComponent, {
      data: { message },
    }).then((result) => result ?? false);
  }

  /**
   * Shows a blocking "session expired" prompt. Resolves once the user clicks "Log in again";
   * the caller is responsible for logging out / redirecting to the login screen.
   */
  public async sessionExpired(): Promise<void> {
    await this.open<SessionExpiredDialogInput, SessionExpiredDialogResult>(
      SessionExpiredDialogComponent,
      { autoFocus: false },
    );
  }

  /**
   * Open a Material Dialog with extra type safety
   *
   * This function forces to define the input/output types of the dialog:
   * - Input data type
   * - Result data type
   *
   * This is specially interesting in strict mode because the default generic for a dialog return type is `any`.
   * So when using `afterClosed`, it returns `any | undefined`, which TS resolves as `any` (hence we loose type safety).
   * By forcing an `Output` type we ensure we'll get `T | undefined` and we'll be forced to handle the `undefined` case in the components.
   *
   * @template Input Input data for the dialog.
   * @template Output Resulting output data type of the dialog. Note that the dialog will return `Output | undefined`.
   * @param component Dialog class to open.
   * @param config MatDialog configuration, same as in regular `MatDialog.open`
   */
  public open<Input, Output>(
    component: Type<unknown>,
    config: MatDialogConfig<Input> = {},
  ): Promise<Output | undefined> {
    // Remove focus on active element
    // Thanks: https://stackoverflow.com/questions/79159883/warning-blocked-aria-hidden-on-an-element-because-its-descendant-retained-focu
    const activeElement = document.activeElement as HTMLElement | null;

    if (activeElement?.blur) {
      activeElement.blur();
    }

    // Open dialog
    const dialogRef = this.dialog.open(component, {
      panelClass: ['mo-dialog'],
      width: 'calc(100% - 24px)',
      maxWidth: '400px',
      ...config,
    });

    this.dialogStack.update((v) => v + 1);
    const result = lastValueFrom(dialogRef.afterClosed());
    result.finally(() => this.dialogStack.update((v) => v - 1));

    return result;
  }

  public showErrorAlert(err: unknown, fallbackMsg: string): void {
    let fallbackMessage = this.translate.instant(fallbackMsg);
    let message: string;

    // Fallback to a generic message
    if (fallbackMessage === fallbackMsg) {
      fallbackMessage = this.translate.instant('error.fallback.generic');
    }

    if (isFirebaseFunctionsError(err)) {
      const errorKey = `error.type.${err.message}`;
      message = this.translate.instant(errorKey, err.details);

      // Fallback to fallback message if the translation is missing
      if (message === errorKey) {
        message = fallbackMessage;
      }
    } else if (err instanceof FriendlyError) {
      const errorKey = `error.friendly.${err.key}`;
      message = this.translate.instant(errorKey);

      // Fallback to fallback message if the translation is missing
      if (message === errorKey) {
        message = fallbackMessage;
      }
    } else {
      message = fallbackMessage;
    }

    this.alert(message, MessageType.Error, formatError(err));
  }

  public snack(msg: string, type: MessageType = MessageType.Info): void {
    const classMap: Record<MessageType, string> = {
      [MessageType.Error]: 'mo-snack-error',
      [MessageType.Info]: 'mo-snack-info',
      [MessageType.Warning]: 'mo-snack-warning',
      [MessageType.Success]: 'mo-snack-success',
    };

    this.snackBar.open(this.translate.instant(msg), undefined, {
      duration: 8000,
      panelClass: ['mo-snack', classMap[type]],
    });
  }

  public wait(message?: string): () => void {
    const closeDeferred = createDeferred<void>();

    this.open<WaitDialogInput, WaitDialogResult>(WaitDialogComponent, {
      data: { close: closeDeferred.promise, message },
      disableClose: true,
    });

    return () => closeDeferred.resolve();
  }
}
